import { momentIntl } from 'src/i18n';
import { capitalizeFirstLetter } from 'src/shared/utils/string';
import moment from 'moment';

type MonthAndYear = {
  month: number;
  year: number;
};

export const getCurrentMonthFirstDayDate = (nowTimestamp: Date): Date => {
  const nowFirstMonthDayTimestamp = new Date(nowTimestamp).setDate(1);

  return new Date(nowFirstMonthDayTimestamp);
};

const getLast12Months = (): MonthAndYear[] => {
  let last12Months: MonthAndYear[] = [];

  for (let monthIndex = 11; monthIndex >= 0; monthIndex--) {
    const now = new Date(Date.now());
    const nowFirstMonthDayDate = getCurrentMonthFirstDayDate(now);
    const newDate = nowFirstMonthDayDate.setMonth(nowFirstMonthDayDate.getMonth() - monthIndex);

    last12Months = [...last12Months, { month: moment(newDate).month(), year: moment(newDate).year() }];
  }

  return last12Months;
};

export type Payroll = {
  saveflowTransactionsCount?: number;
  saveflowTransactionsAmount?: number;
  payflowProcessedTransactionCount: number;
  payflowProcessedTransactionAmount: number;
  extraPaymentsTransactionCount?: number;
  extraPaymentsTransactionAmount?: number;
  monthRequestedAt: number;
  yearRequestedAt: number;
  payflowProcessingTransactionCount?: number;
  payflowProcessingTransactionAmount?: number;
};

export type PayrollChartData = {
  saveflowTransactionsCount?: number;
  saveflowTransactionsAmount?: number;
  payflowPlusProcessingTransactionCount?: number;
  payflowPlusProcessingTransactionAmount?: number;
  extraPaymentsTransactionCount?: number;
  extraPaymentsTransactionAmount?: number;
  label: string;
};

export const calculatePayflowTransactions = (
  processedTransactions: undefined | number,
  processingTransactions: undefined | number
): undefined | number => {
  if (processedTransactions === undefined && processingTransactions === undefined) {
    return undefined;
  }

  if (processedTransactions && processingTransactions === undefined) {
    return processedTransactions;
  }

  if (processedTransactions === undefined && processingTransactions) {
    return processingTransactions;
  }

  return Number(processedTransactions) + Number(processingTransactions);
};

export const formatPayrollData = (payrolls: Payroll[] = [], now = new Date()): PayrollChartData[] => {
  const last12Months = getLast12Months();
  const result = last12Months.map(({ month, year }) => {
    const maybePayroll = payrolls.find(payroll => {
      if (payroll.monthRequestedAt === month + 1 && payroll.yearRequestedAt === year) {
        return payroll;
      }
    });

    const monthName = momentIntl.months()[month];
    const lastTwoDigitsOfAYear = `${year}`.slice(-2);
    const displayedLabel = `${capitalizeFirstLetter(monthName)} ${lastTwoDigitsOfAYear}`;

    if (maybePayroll) {
      return {
        saveflowTransactionsCount: maybePayroll?.saveflowTransactionsCount,
        saveflowTransactionsAmount: maybePayroll?.saveflowTransactionsAmount,
        payflowPlusProcessingTransactionCount: calculatePayflowTransactions(
          maybePayroll?.payflowProcessedTransactionCount,
          maybePayroll?.payflowProcessingTransactionCount
        ),
        payflowPlusProcessingTransactionAmount: calculatePayflowTransactions(
          maybePayroll?.payflowProcessedTransactionAmount,
          maybePayroll?.payflowProcessingTransactionAmount
        ),
        extraPaymentsTransactionCount: maybePayroll?.extraPaymentsTransactionCount,
        extraPaymentsTransactionAmount: maybePayroll?.extraPaymentsTransactionAmount,
        label: displayedLabel
      };
    }

    return {
      saveflowTransactions: undefined,
      saveflowTransactionsAmount: undefined,
      payflowPlusProcessingTransactionCount: undefined,
      payflowPlusProcessingTransactionAmount: undefined,
      extraPaymentsTransactionCount: undefined,
      extraPaymentsTransactionAmount: undefined,
      label: displayedLabel
    };
  });

  return result;
};
